import {z} from 'zod';

import {BaseVoidResponseSchema, PriceWithAndWithoutVatSchema} from '@dms/api/shared';

/**
 * Base
 */
const BaseIdAndNameSchema = z.object({
  id: z.string(),
  name: z.string(),
});

const ServiceOrderHeaderSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  customerId: z.string(),
  vehicleId: z.string(),
  number: z.string(),
  type: BaseIdAndNameSchema,
  variant: BaseIdAndNameSchema,
});

const DirectSaleHeaderSchema = z.object({
  directSaleId: z.string(),
  customerId: z.string().nullish(),
  vehicleId: z.string().nullish(),
  number: z.string(),
  type: BaseIdAndNameSchema,
  variant: BaseIdAndNameSchema,
});

/**
 * GET
 */
export const GetFastTrackIssueRequestSchema = z.object({
  fastTrackId: z.string(),
});
export type GetFastTrackIssueRequest = z.infer<typeof GetFastTrackIssueRequestSchema>;

export const GetFastTrackIssueResponseSchema = z.object({
  fastTrackId: z.string(),
  state: z.enum(['PENDING', 'FAST_TRACK_ISSUE', 'COMPLETED']),
  number: z.string(),
  note: z.string().nullish(),
  createdAt: z.string(),
  createdBy: z.string(),
  fastTrackIssuedAt: z.string().nullish(),
  fastTrackIssuedBy: z.string().nullish(),
  completedAt: z.string().nullish(),
  completedBy: z.string().nullish(),
  receivedAt: z.string().nullish(),
  receivedBy: z.string().nullish(),
  originEntityHeader: z.object({
    serviceOrder: ServiceOrderHeaderSchema.nullish(),
    directSale: DirectSaleHeaderSchema.nullish(),
  }),
  totalPrice: PriceWithAndWithoutVatSchema,
});
export type GetFastTrackIssueResponse = z.infer<typeof GetFastTrackIssueResponseSchema>;

export const GetFastTrackBasketRequestSchema = z.object({
  fastTrackId: z.string(),
});
export type GetFastTrackBasketRequest = z.infer<typeof GetFastTrackBasketRequestSchema>;

export const FastTrackBasketItemSchema = z.object({
  id: z.string(),
  warehouseId: z.string(),
  itemEditingAllowed: z.boolean(),
  requestType: z.string().optional(),
  name: z.string(),
  manufacturerNumber: z.string(),
  manufacturerId: z.string(),
  quantity: z.number(),
  unit: z.string().nullish(),
  dispensingUnit: z.number(),
  quantityEditingAllowed: z.boolean(),
  tooltip: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
    })
  ),
  minMaxQuantity: z.object({
    minQuantity: z.number(),
    maxQuantity: z.number(),
  }),
});

export type FastTrackBasketItem = z.infer<typeof FastTrackBasketItemSchema>;

export const GetFastTrackBasketResponseSchema = z.object({
  fastTrackBasketItem: z.array(FastTrackBasketItemSchema),
});
export type GetFastTrackBasketResponse = z.infer<typeof GetFastTrackBasketResponseSchema>;

/**
 * POST
 */
export const PostFastTrackIssueRequestSchema = z.object({
  originEntityHeaderId: z.string(),
  originEntityHeaderType: z.enum(['direct-sale', 'service-case-order']),
  note: z.string().nullish(),
});
export type PostFastTrackIssueRequest = z.infer<typeof PostFastTrackIssueRequestSchema>;

export const PostFastTrackIssueResponseSchema = z.object({
  fastTrackId: z.string(),
});
export type PostFastTrackIssueResponse = z.infer<typeof PostFastTrackIssueResponseSchema>;

export const PostFastTrackIssueBasketDiscardRequestSchema = z.object({
  fastTrackId: z.string(),
});
export type PostFastTrackIssueBasketDiscardRequest = z.infer<
  typeof PostFastTrackIssueBasketDiscardRequestSchema
>;

export const PostFastTrackIssueBasketDiscardResponseSchema = BaseVoidResponseSchema;
export type PostFastTrackIssueBasketDiscardResponse = z.infer<
  typeof PostFastTrackIssueBasketDiscardResponseSchema
>;

export const PostFastTrackIssueBasketCheckoutRequestSchema = z.object({
  fastTrackId: z.string(),
});
export type PostFastTrackIssueBasketCheckoutRequest = z.infer<
  typeof PostFastTrackIssueBasketCheckoutRequestSchema
>;

export const PostFastTrackIssueBasketCheckoutResponseSchema = BaseVoidResponseSchema;
export type PostFastTrackIssueBasketCheckoutResponse = z.infer<
  typeof PostFastTrackIssueBasketCheckoutResponseSchema
>;

export const PostFastTrackIssueBasketAddRequestSchema = z.object({
  fastTrackId: z.string(),
  body: z.object({
    items: z.array(
      z.object({
        originEntityItemId: z.string(),
        originEntityItemType: z.string(),
      })
    ),
  }),
});
export type PostFastTrackIssueBasketAddRequest = z.infer<
  typeof PostFastTrackIssueBasketAddRequestSchema
>;

export const PostFastTrackIssueBasketAddResponseSchema = z.object({
  fastTrackItem: z.array(
    z.object({
      fastTrackItemId: z.string(),
    })
  ),
});
export type PostFastTrackIssueBasketAddResponse = z.infer<
  typeof PostFastTrackIssueBasketAddResponseSchema
>;

/**
 * PATCH
 */
export const PatchFastTrackIssueRequestSchema = z.object({
  fastTrackId: z.string(),
  body: z.object({
    note: z.string().nullish(),
  }),
});
export type PatchFastTrackIssueRequest = z.infer<typeof PatchFastTrackIssueRequestSchema>;

export const PatchFastTrackIssueResponseSchema = BaseVoidResponseSchema;
export type PatchFastTrackIssueResponse = z.infer<typeof PatchFastTrackIssueResponseSchema>;

export const PatchFastTrackIssueBasketItemQuantityRequestSchema = z.object({
  fastTrackId: z.string(),
  fastTrackItemId: z.string(),
  body: z.object({
    quantity: z.number(),
  }),
});
export type PatchFastTrackIssueBasketItemQuantityRequest = z.infer<
  typeof PatchFastTrackIssueBasketItemQuantityRequestSchema
>;

export const PatchFastTrackIssueBasketItemQuantityResponseSchema = BaseVoidResponseSchema;
export type PatchFastTrackIssueBasketItemQuantityResponse = z.infer<
  typeof PatchFastTrackIssueBasketItemQuantityResponseSchema
>;

/**
 * DELETE
 */
export const DeleteFastTrackIssueRequestSchema = z.object({
  fastTrackIds: z.array(z.string()),
});
export type DeleteFastTrackIssueRequest = z.infer<typeof DeleteFastTrackIssueRequestSchema>;

export const DeleteFastTrackIssueResponseSchema = BaseVoidResponseSchema;
export type DeleteFastTrackIssueResponse = z.infer<typeof DeleteFastTrackIssueResponseSchema>;

export const DeleteFastTrackIssueBasketItemRequestSchema = z.object({
  fastTrackId: z.string(),
  body: z.object({
    fastTrackItemIds: z.array(z.string()),
  }),
});
export type DeleteFastTrackIssueBasketItemRequest = z.infer<
  typeof DeleteFastTrackIssueBasketItemRequestSchema
>;

export const DeleteFastTrackIssueBasketItemResponseSchema = BaseVoidResponseSchema;
export type DeleteFastTrackIssueBasketItemResponse = z.infer<
  typeof DeleteFastTrackIssueBasketItemResponseSchema
>;

export const PostFastTrackItemCompleteRequestSchema = z.object({
  fastTrackId: z.string(),
  body: z.object({
    fastTrackItemIds: z.array(z.string()),
  }),
});
export type PostFastTrackItemCompleteRequest = z.infer<
  typeof PostFastTrackItemCompleteRequestSchema
>;

export const PostFastTrackItemCompleteResponseSchema = BaseVoidResponseSchema;
export type PostFastTrackItemCompleteResponse = z.infer<
  typeof PostFastTrackItemCompleteResponseSchema
>;

/**
 * PUT
 */
